// Tech Support Co-Pilot — resolve the page's send-status banner from the
// server's runtime config (/portal/config), so the copy always reflects what
// the Send button actually does in THIS environment:
//   - kill-switch off            → drafts-only "testing mode" notice
//   - sandbox + redirect set     → sends really deliver, to the test address
//   - sandbox, no redirect       → sends are suppressed
//   - live (prod, switch on)     → no banner
import type { PortalConfig } from '../services/portal';

export type SendBannerKind = 'drafts-only' | 'sandbox-redirect' | 'sandbox-suppressed';

export interface SendBanner {
  kind: SendBannerKind;
  text: string;
}

export function resolveSendBanner(config: PortalConfig | null): SendBanner | null {
  if (!config) {
    // Config not loaded yet — render NO banner. Showing a provisional
    // "testing mode" strip here made it flash on-then-off in prod (where live
    // sending is on and the correct state is no banner). A brief no-banner
    // moment is strictly better than a wrong-promise flash; the real state
    // resolves within one round-trip.
    return null;
  }
  if (!config.tech_support_send_enabled) {
    return {
      kind: 'drafts-only',
      text: 'Testing mode — Merlin drafts replies for review but does not actually send emails yet.',
    };
  }
  if (config.sandbox_mode) {
    if (config.sandbox_redirect_email) {
      return {
        kind: 'sandbox-redirect',
        text:
          `Sandbox forwarding — Send really delivers, but every email is redirected to ` +
          `${config.sandbox_redirect_email}. Real recipients are never emailed from this environment.`,
      };
    }
    return {
      kind: 'sandbox-suppressed',
      text: 'Sandbox — sends are recorded but not delivered (no redirect address configured).',
    };
  }
  // Live sending (production with the switch on): no banner.
  return null;
}
